import React from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";
import { Link2, Twitter } from "lucide-react";
import { copyText } from "../lib/copyText";

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export const ProposalDialog = ({ proposal, onClose }) => {
  if (!proposal) return null;

  const { tag, headline, sectionTitle, intro, itemsTitle, closing, quote, sources, stateTableNote, timelineTitle } = proposal;
  const highlights = proposal.highlights || [];
  const items = proposal.items || [];
  const stats = proposal.stats || [];
  const stateTable = proposal.stateTable || [];
  const timeline = proposal.timeline || [];
  const highlightGridClass = highlights.length > 1 ? "grid gap-4 md:grid-cols-2" : "grid gap-4";

  const shareUrl = `${window.location.origin}/?proposta=${proposal.id}`;
  const shareText = `${headline} Conheça a proposta de Serra Neto 3084: ${shareUrl}`;

  const copyLink = async () => {
    const ok = await copyText(shareUrl);
    toast[ok ? "success" : "error"](ok ? "Link da proposta copiado!" : "Não foi possível copiar o link");
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto p-0 gap-0" data-testid="proposal-dialog">
        {/* Header */}
        <div className="bg-novo-navy text-white p-6 md:p-8">
          <p className="font-dm text-novo-orange text-xs font-semibold uppercase tracking-wider mb-2">
            Material Programático 2026 · {tag}
          </p>
          <DialogTitle className="font-oswald text-2xl md:text-4xl font-bold uppercase leading-tight text-white" data-testid="proposal-dialog-headline">
            {headline}
          </DialogTitle>
          <DialogDescription className="font-dm text-slate-300 text-sm mt-3">
            Menos deputados, mais resultados. · Serra Neto · Candidato a Deputado Federal · SP · 2026 · Partido Novo
          </DialogDescription>
        </div>

        <div className="p-6 md:p-8 space-y-6">
          <div>
            <h3 className="font-oswald text-xl md:text-2xl font-bold text-novo-navy uppercase mb-3">
              {sectionTitle}
            </h3>
            <p className="font-dm text-slate-600 leading-relaxed">{intro}</p>
          </div>

          <div className={highlightGridClass}>
            {highlights.map((h, i) => (
              <div key={i} className="bg-slate-50 border-l-4 border-novo-orange p-5">
                <p className="font-oswald text-3xl font-bold text-novo-orange">{h.number}</p>
                <p className="font-dm text-sm text-slate-600 mt-2 leading-relaxed">{h.text}</p>
              </div>
            ))}
          </div>

          <div>
            <h4 className="font-oswald text-lg font-bold text-novo-navy uppercase mb-4 border-b-2 border-novo-orange inline-block pb-1">
              {itemsTitle}
            </h4>
            <div className="space-y-4">
              {items.map((item, i) => (
                <div key={i}>
                  <p className="font-dm font-bold text-novo-navy">{item.title}</p>
                  <p className="font-dm text-sm text-slate-600 leading-relaxed mt-1">{item.text}</p>
                </div>
              ))}
            </div>
          </div>

          {stats.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {stats.map((s, i) => (
                <div key={i} className="bg-novo-navy p-4 text-center">
                  <p className="font-oswald text-xl font-bold text-novo-orange">{s.number}</p>
                  <p className="font-dm text-[11px] text-slate-300 mt-1 leading-snug">{s.label}</p>
                </div>
              ))}
            </div>
          )}

          {stateTable.length > 0 && (
            <div data-testid="state-table">
              <h4 className="font-oswald text-lg font-bold text-novo-navy uppercase mb-3 border-b-2 border-novo-orange inline-block pb-1">
                A conta estado a estado · de 513 para 168
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm font-dm">
                  <thead>
                    <tr className="bg-novo-navy text-white">
                      <th className="text-left px-3 py-2 font-oswald uppercase text-xs tracking-wider">Estado</th>
                      <th className="text-center px-3 py-2 font-oswald uppercase text-xs tracking-wider">Hoje</th>
                      <th className="text-center px-3 py-2 font-oswald uppercase text-xs tracking-wider">Nova</th>
                      <th className="text-center px-3 py-2 font-oswald uppercase text-xs tracking-wider">Var.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stateTable.map((row, i) => (
                      <tr key={row.uf} className={i % 2 === 0 ? "bg-slate-50" : "bg-white"}>
                        <td className={`px-3 py-1.5 font-semibold ${row.uf === "SP" ? "text-novo-orange" : "text-novo-navy"}`}>{row.uf}</td>
                        <td className="px-3 py-1.5 text-center text-slate-600">{row.hoje}</td>
                        <td className="px-3 py-1.5 text-center font-bold text-novo-navy">{row.nova}</td>
                        <td className="px-3 py-1.5 text-center text-slate-500">{row.nova - row.hoje}</td>
                      </tr>
                    ))}
                    <tr className="bg-novo-navy text-white font-bold">
                      <td className="px-3 py-2 font-oswald uppercase">Total Brasil</td>
                      <td className="px-3 py-2 text-center">513</td>
                      <td className="px-3 py-2 text-center text-novo-orange">168</td>
                      <td className="px-3 py-2 text-center">-345</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="font-dm text-[11px] text-slate-400 mt-2 leading-relaxed">{stateTableNote}</p>
            </div>
          )}

          {timeline.length > 0 && (
            <div data-testid="proposal-timeline">
              <h4 className="font-oswald text-lg font-bold text-novo-navy uppercase mb-4 border-b-2 border-novo-orange inline-block pb-1">
                {timelineTitle}
              </h4>
              <div className="space-y-0">
                {timeline.map((step, i) => (
                  <div key={i} className="flex gap-4 relative pb-5">
                    {i < timeline.length - 1 && (
                      <div className="absolute left-[52px] top-6 bottom-0 w-px bg-slate-200"></div>
                    )}
                    <div className="w-[105px] shrink-0 text-right">
                      <span className="inline-block bg-novo-navy text-novo-orange font-oswald font-bold text-xs px-2 py-1 uppercase">{step.when}</span>
                    </div>
                    <div>
                      <p className="font-dm font-bold text-novo-navy text-sm">{step.title}</p>
                      <p className="font-dm text-xs text-slate-500 leading-relaxed mt-1">{step.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {closing && (
            <p className="font-dm text-sm text-slate-600 leading-relaxed">{closing}</p>
          )}

          <div className="bg-novo-orange/10 border-l-4 border-novo-orange p-5">
            <p className="font-oswald text-lg text-novo-navy font-semibold leading-snug">
              "{quote}"
            </p>
            <p className="font-dm text-xs text-slate-500 mt-2">— Serra Neto</p>
          </div>

          {/* Share */}
          <div className="border-t border-slate-200 pt-5" data-testid="proposal-share">
            <p className="font-oswald text-sm font-bold text-novo-navy uppercase mb-3">Compartilhe esta proposta</p>
            <div className="flex flex-wrap gap-3">
              <a
                href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white px-4 py-2 text-sm font-dm font-semibold hover:opacity-90 transition-opacity duration-200"
                data-testid="share-whatsapp"
              >
                <WhatsAppIcon /> WhatsApp
              </a>
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-black text-white px-4 py-2 text-sm font-dm font-semibold hover:opacity-90 transition-opacity duration-200"
                data-testid="share-x"
              >
                <Twitter size={16} /> X
              </a>
              <button
                type="button"
                onClick={copyLink}
                className="inline-flex items-center gap-2 bg-novo-navy text-white px-4 py-2 text-sm font-dm font-semibold hover:opacity-90 transition-opacity duration-200"
                data-testid="share-copy-link"
              >
                <Link2 size={16} /> Copiar link
              </button>
            </div>
          </div>

          <p className="font-dm text-[11px] text-slate-400 leading-relaxed border-t border-slate-200 pt-4">
            {sources}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
